import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { AuthContext } from '../context/AuthContext';
import api, { getAdminApi } from '../services/api';

const CATEGORIES = [
  { value: 'features', label: 'Features' },
  { value: 'billing', label: 'Billing' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'how-to', label: 'How-To' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' }
];

const AdminSupportTicket = () => {
  const { ticketId } = useParams();
  const navigate = useAppNavigation();
  const { currentUser } = useContext(AuthContext);
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('other');
  const [tags, setTags] = useState('');

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const response = await adminApi.get(`/support/tickets/${ticketId}`);
      setTicket(response.data.ticket);
      
      // Pre-fill category if available
      if (response.data.ticket.category) {
        setCategory(response.data.ticket.category);
      }
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(err.response?.data?.error || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!answer.trim()) {
      setError('Please provide an answer');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const response = await adminApi.post(`/support/tickets/${ticketId}/reply`, {
        answer: answer.trim(),
        category,
        tags: tags.split(',').map(t => t.trim()).filter(t => t)
      });
      
      if (response.data.success) {
        setSuccess(true);
        // Refresh ticket to show updated status
        fetchTicket();
      } else {
        setError(response.data.error || 'Failed to submit reply');
      }
    } catch (err) {
      console.error('Error submitting reply:', err);
      setError(err.response?.data?.error || 'Failed to submit reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'info';
      case 'replied': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Support Ticket #{ticketId}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Reply submitted successfully! The answer has been added to the knowledge base.
          </Alert>
        )}

        {ticket ? (
          <>
            {/* Ticket Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Question</Typography>
                <Chip 
                  label={ticket.status} 
                  color={getStatusColor(ticket.status)}
                  size="small"
                />
              </Box>
              
              <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                {ticket.question}
              </Typography>
              
              {ticket.context && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                    Context:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.context}
                  </Typography>
                </>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  User ID: {ticket.user_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(ticket.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Paper>

            {/* Reply Form */}
            {ticket.status !== 'replied' && ticket.status !== 'closed' ? (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Submit Answer
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Provide a clear and helpful answer to the user's question..."
                    sx={{ mb: 2 }}
                    required
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Tags (comma-separated)"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="api, integration, feature-request"
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={submitting || !answer.trim()}
                    startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {submitting ? 'Submitting...' : 'Submit Answer'}
                  </Button>
                </form>
              </Paper>
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Reply
                </Typography>
                <Typography variant="body1" sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                  {ticket.reply_content}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Replied by: {ticket.replied_by} on {new Date(ticket.replied_at).toLocaleString()}
                </Typography>
              </Paper>
            )}
          </>
        ) : (
          <Alert severity="error">Ticket not found</Alert>
        )}
      </Container>
    </Box>
  );
};

export default AdminSupportTicket;
